import {ApiClient, type ErrorResponse} from '@hconnect/apiclient'
import {type AttachmentFile} from '@hconnect/common/types'
import {attachmentFilesToFormData} from '@hconnect/common/utils/formData.utils'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, type UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useErrorHandler} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {notifyIfErrorMessage} from '../utils'

type Variables = {
  plantCode: string
  materialOrderId: number
  attachments: AttachmentFile[]
}

export const useAddMaterialOrderAttachments = (
  options?: Omit<UseMutationOptions<void, AxiosError<ErrorResponse>, Variables>, 'mutationFn'>
) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const raiseError = useErrorHandler()
  const queryClient = useQueryClient()
  return useMutation<void, AxiosError<ErrorResponse>, Variables>({
    ...options,
    mutationKey: ['addMaterialOrderAttachments'],
    mutationFn: async ({plantCode, materialOrderId, attachments}) => {
      const path = `/plants/${plantCode}/material-orders/${materialOrderId}/attachments`
      const formData = new FormData()
      await attachmentFilesToFormData(attachments, formData)
      await ApiClient.axiosInstance.post<void>(path, formData)
    },
    onError: (error) => {
      notifyIfErrorMessage(error.response?.data.detail, raiseError, spawnErrorToast)
    },
    onSuccess: () => {
      spawnSuccessToast(t('success.addMaterialOrderAttachments'))
    },
    onSettled: () => {
      void queryClient.invalidateQueries({queryKey: ['materialOrders'], refetchType: 'all'})
    }
  })
}
